import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { hotComments, talkStats } from '@/mocks/home';

export default function HotTalk() {
  const [likedIds, setLikedIds] = useState<string[]>([]);

  const toggleLike = (id: string) => {
    setLikedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  return (
    <section id="talk" className="scroll-mt-24 bg-background-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
        <Reveal>
          <SectionHeading
            index="03"
            eyebrow="THE CONVERSATION"
            title="大家都在聊"
            description="每条动态下面的评论，往往比正文更热闹。这里挑出本周讨论最多的几条，看看别人是怎么跟进黑料官方动态的。"
          />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:mt-16 lg:grid-cols-12 lg:gap-8">
          <Reveal className="lg:col-span-7">
            <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <div className="flex items-center justify-between gap-4 border-b border-background-200 pb-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-discuss-line text-xl"></i>
                  </span>
                  <div>
                    <p className="font-heading text-base font-semibold text-foreground-950">
                      本周评论区
                    </p>
                    <p className="mt-0.5 text-xs text-foreground-500">数据每小时刷新一次</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
                  <i className="ri-fire-line text-sm"></i>
                  热度上升
                </span>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4">
                {talkStats.map((stat) => (
                  <div key={stat.label} className="rounded-lg bg-background-50 px-4 py-4">
                    <p className="font-heading text-xl font-bold text-primary-600 md:text-2xl">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-5">
                {hotComments.map((comment) => {
                  const liked = likedIds.includes(comment.id);
                  return (
                    <article
                      key={comment.id}
                      className="rounded-lg border border-background-200 bg-background-50 p-5"
                    >
                      <div className="flex items-start gap-4">
                        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full">
                          <img
                            src={comment.avatar}
                            alt={`${comment.name} 的头像`}
                            title="黑料官方读者头像"
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="text-sm font-medium text-foreground-950">
                              {comment.name}
                            </span>
                            <span className="text-xs text-foreground-500">{comment.platform}</span>
                            <span className="text-xs text-foreground-400">{comment.time}</span>
                          </div>
                          <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                            {comment.text}
                          </p>
                          <div className="mt-4 flex items-center gap-5 text-xs text-foreground-500">
                            <button
                              type="button"
                              onClick={() => toggleLike(comment.id)}
                              className={`flex items-center gap-1.5 transition-colors ${
                                liked ? 'text-primary-600' : 'hover:text-primary-600'
                              }`}
                            >
                              <i className={liked ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'}></i>
                              {liked ? comment.likes + 1 : comment.likes}
                            </button>
                            <span className="flex items-center gap-1.5">
                              <i className="ri-chat-3-line text-base"></i>
                              {comment.replies}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <i className="ri-share-forward-line text-base"></i>
                              分享
                            </span>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div className="relative flex h-full min-h-[320px] flex-col justify-end overflow-hidden rounded-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20abstract%20conversation%20themed%20editorial%20backdrop%20with%20soft%20crimson%20and%20sand%20bokeh%20circles%2C%20layered%20paper%20texture%2C%20artistic%20non%20realistic%20composition%2C%20cozy%20warm%20lighting%2C%20high%20detail%2C%20muted%20elegant%20palette&width=900&height=1100&seq=hlgl-talk-01&orientation=portrait"
                alt="黑料官方读者讨论氛围配图"
                title="黑料官方 评论区讨论"
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/40 to-foreground-950/10"></div>
              <div className="relative z-10 p-6 md:p-8">
                <span className="font-label text-xs tracking-[0.3em] text-accent-300">
                  READER VOICES
                </span>
                <p className="mt-4 font-heading text-xl font-semibold leading-snug text-background-50 md:text-2xl">
                  「四个渠道跟着看了一周，终于不会再漏掉任何一条了。」
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <span className="h-px w-8 bg-background-50/50" aria-hidden="true"></span>
                  <span className="text-xs text-background-100">来自微博读者 · 上周</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}